import { VerticalSlug } from './types'

export interface CommercialRoleOption {
  value: string
  label: string
  icon: string
}

export interface VerticalCommercialRoleConfig {
  options: CommercialRoleOption[]
  helperText: string
  complementsTitle: string
  complementsDescription: string
  crossSellingTitle?: string
  crossSellingDescription?: string
}

const RESTAURANTE_CONFIG: VerticalCommercialRoleConfig = {
  options: [
    { value: '', label: 'General / Sin rol específico', icon: '📦' },
    { value: 'main', label: 'Plato Principal', icon: '🍔' },
    { value: 'drink', label: 'Bebida', icon: '🥤' },
    { value: 'side', label: 'Acompañante / Entrada / Extra', icon: '🍟' },
    { value: 'dessert', label: 'Postre', icon: '🍰' },
    { value: 'accessory', label: 'Adicional / Salsa / Topping', icon: '🧀' },
    { value: 'service', label: 'Servicio / Delivery Especial', icon: '🛵' },
  ],
  helperText:
    'Permite al asistente recomendar este ítem como plato principal, bebida, acompañante o postre en sugerencias de combos.',
  complementsTitle: 'Acompañantes y Adicionales Sugeridos',
  complementsDescription:
    'Productos que el bot sugerirá automáticamente cuando el cliente elija este plato (bebidas, entradas o extras).',
  crossSellingTitle: 'Sugerir bebidas y acompañantes (Combos / Cross-selling)',
  crossSellingDescription:
    'El bot sugerirá bebidas, acompañantes o postres para armar combos antes de finalizar el pedido si están configurados en tus productos.',
}

const REPUESTOS_CONFIG: VerticalCommercialRoleConfig = {
  options: [
    { value: '', label: 'General / Sin rol específico', icon: '📦' },
    { value: 'main', label: 'Repuesto Principal / Pieza Base', icon: '⚙️' },
    { value: 'accessory', label: 'Consumible / Accesorio (Filtro, Grasa, Liga)', icon: '🔩' },
    { value: 'substitute', label: 'Reemplazo Alternativo / Genérico', icon: '🔄' },
    { value: 'service', label: 'Servicio / Instalación / Montaje', icon: '🛠️' },
    { value: 'warranty', label: 'Garantía Extendida', icon: '🛡️' },
  ],
  helperText:
    'Permite al asistente recomendar consumibles (ej: liga con pastillas), piezas complementarias o servicios de montaje junto al repuesto principal en WhatsApp.',
  complementsTitle: 'Consumibles y Complementos Sugeridos',
  complementsDescription:
    'Ítems que el bot ofrecerá al cotizar este repuesto (ej: liga de frenos, filtros, grasa o empacaduras).',
  crossSellingTitle: 'Sugerir consumibles y repuestos complementarios (Cross-selling)',
  crossSellingDescription:
    'El bot sugerirá consumibles (ej: liga con pastillas, filtros, lubricantes) o repuestos complementarios antes de cerrar la cotización.',
}

const BODEGON_CONFIG: VerticalCommercialRoleConfig = {
  options: [
    { value: '', label: 'General / Sin rol específico', icon: '📦' },
    { value: 'main', label: 'Licor / Producto Principal', icon: '🍾' },
    { value: 'drink', label: 'Mezclador / Refresco / Jugo', icon: '🥤' },
    { value: 'side', label: 'Snack / Pasapalos / Hielo', icon: '🍿' },
    { value: 'accessory', label: 'Accesorio / Vasos / Descorche', icon: '🧊' },
    { value: 'service', label: 'Servicio / Despacho Nocturno', icon: '🛵' },
  ],
  helperText:
    'Permite al asistente armar combos sugeridos de licores con hielo, mezcladores y snacks en WhatsApp.',
  complementsTitle: 'Mezcladores, Hielo y Snacks Sugeridos',
  complementsDescription:
    'Ítems que el bot sugerirá para armar el combo (hielo, refrescos, vasos o pasapalos).',
  crossSellingTitle: 'Sugerir hielo, mezcladores y snacks (Combos / Cross-selling)',
  crossSellingDescription:
    'El bot sugerirá refrescos, hielo o snacks para armar combos antes de cerrar el pedido si están configurados en tus productos.',
}

const FERRETERIA_CONFIG: VerticalCommercialRoleConfig = {
  options: [
    { value: '', label: 'General / Sin rol específico', icon: '📦' },
    { value: 'main', label: 'Material o Herramienta Principal', icon: '🔨' },
    { value: 'accessory', label: 'Insumo / Accesorio (Tornillos, Mechas, Lijadora)', icon: '🪛' },
    { value: 'substitute', label: 'Alternativa / Marca Sustituta', icon: '🔄' },
    { value: 'service', label: 'Flete Pesado / Descarga', icon: '🚚' },
    { value: 'warranty', label: 'Garantía de Fábrica', icon: '🛡️' },
  ],
  helperText:
    'Permite al asistente sugerir consumibles de obra, tornillería o flete pesado al cotizar materiales.',
  complementsTitle: 'Insumos y Accesorios de Obra Sugeridos',
  complementsDescription:
    'Tornillería, lijas, brocas o materiales complementarios que el bot sugerirá al cliente.',
  crossSellingTitle: 'Sugerir consumibles y herramientas de obra (Cross-selling)',
  crossSellingDescription:
    'El bot sugerirá tornillos, lijas, brocas o materiales complementarios antes de finalizar la compra si están configurados.',
}

const TECNOLOGIA_CONFIG: VerticalCommercialRoleConfig = {
  options: [
    { value: '', label: 'General / Sin rol específico', icon: '📦' },
    { value: 'main', label: 'Dispositivo / Equipo Principal', icon: '💻' },
    { value: 'accessory', label: 'Accesorio / Forro / Vidrio / Cable', icon: '🔌' },
    { value: 'substitute', label: 'Alternativa / Gama Similar', icon: '📱' },
    { value: 'warranty', label: 'Garantía Extendida / Seguro', icon: '🛡️' },
    { value: 'service', label: 'Servicio Técnico / Configuración', icon: '🛠️' },
  ],
  helperText:
    'Permite al asistente sugerir forros, vidrios templados, cargadores o garantía extendida junto al equipo.',
  complementsTitle: 'Accesorios y Protección Sugerida',
  complementsDescription:
    'Forros, vidrios templados, cargadores o garantías que el bot sugerirá al comprar este equipo.',
  crossSellingTitle: 'Sugerir accesorios, protección y garantías (Cross-selling)',
  crossSellingDescription:
    'El bot sugerirá vidrios templados, forros, cables o garantías extendidas antes de cerrar la venta si están configurados.',
}

const SALUD_CONFIG: VerticalCommercialRoleConfig = {
  options: [
    { value: '', label: 'General / Sin rol específico', icon: '📦' },
    { value: 'main', label: 'Consulta / Procedimiento Principal', icon: '🩺' },
    { value: 'service', label: 'Examen / Estudio / Laboratorio', icon: '🔬' },
    { value: 'accessory', label: 'Insumo / Material Médico', icon: '🩹' },
  ],
  helperText:
    'Permite al asistente orientar sobre estudios previos o insumos necesarios antes de la consulta médica.',
  complementsTitle: 'Estudios y Servicios Complementarios',
  complementsDescription:
    'Laboratorios, exámenes preparatorios o insumos que el asistente informará antes de la consulta.',
  crossSellingTitle: 'Recordar estudios previos e insumos necesarios',
  crossSellingDescription:
    'El bot informará sobre laboratorios, exámenes preparatorios o insumos necesarios antes de la consulta.',
}

const BELLEZA_CONFIG: VerticalCommercialRoleConfig = {
  options: [
    { value: '', label: 'General / Sin rol específico', icon: '📦' },
    { value: 'main', label: 'Servicio / Turno Principal', icon: '💇‍♀️' },
    { value: 'accessory', label: 'Tratamiento Adicional / Hidratación / Ampolla', icon: '✨' },
    { value: 'service', label: 'Retoque / Mantenimiento Posterior', icon: '💅' },
  ],
  helperText:
    'Permite al asistente recomendar tratamientos hidratantes, ampollas o servicios complementarios de belleza.',
  complementsTitle: 'Tratamientos y Servicios Adicionales',
  complementsDescription:
    'Hidrataciones, ampollas o servicios de cuidado capilar que el bot sugerirá junto a este turno.',
  crossSellingTitle: 'Sugerir tratamientos y cuidados adicionales',
  crossSellingDescription:
    'El bot sugerirá hidrataciones, ampollas o servicios de cuidado capilar/estético junto al turno.',
}

const INMOBILIARIA_CONFIG: VerticalCommercialRoleConfig = {
  options: [
    { value: '', label: 'General / Sin rol específico', icon: '📦' },
    { value: 'main', label: 'Inmueble / Propiedad en Cartera', icon: '🏠' },
    { value: 'service', label: 'Asesoría Legal / Trámite Notarial', icon: '📋' },
  ],
  helperText:
    'Permite al asistente clasificar inmuebles y servicios de corretaje o asesoría jurídica.',
  complementsTitle: 'Servicios Notariales y Complementarios',
  complementsDescription:
    'Servicios jurídicos, redacción de documentos o gestiones complementarias.',
  crossSellingTitle: 'Ofrecer asesoría legal y gestiones notariales',
  crossSellingDescription:
    'El bot ofrecerá orientación sobre trámites notariales, jurídicos o redacción de documentos.',
}

const TURISMO_CONFIG: VerticalCommercialRoleConfig = {
  options: [
    { value: '', label: 'General / Sin rol específico', icon: '📦' },
    { value: 'main', label: 'Hospedaje / Habitación Principal', icon: '🏨' },
    { value: 'accessory', label: 'Paseo / Lancha a Cayos / Excursión', icon: '🚤' },
    { value: 'service', label: 'Plan de Comidas / Desayuno Extra', icon: '🍽️' },
  ],
  helperText:
    'Permite al asistente ofrecer traslados en lancha, excursiones o comidas al cotizar hospedaje.',
  complementsTitle: 'Paseos y Planes Complementarios',
  complementsDescription:
    'Traslados en lancha a cayos, paseos guiados o comidas que el bot sugerirá al reservar.',
  crossSellingTitle: 'Sugerir excursiones, traslados y comidas',
  crossSellingDescription:
    'El bot sugerirá paseos en lancha a cayos, traslados o desayunos adicionales al cotizar hospedaje.',
}

const EDUCACION_CONFIG: VerticalCommercialRoleConfig = {
  options: [
    { value: '', label: 'General / Sin rol específico', icon: '📦' },
    { value: 'main', label: 'Curso / Programa Principal', icon: '🎓' },
    { value: 'accessory', label: 'Material de Estudio / Guía / Kit', icon: '📚' },
    { value: 'service', label: 'Mentoría / Tutoría Personalizada', icon: '🧑‍🏫' },
  ],
  helperText:
    'Permite al asistente sugerir materiales o tutorías personalizadas para complementar el programa educativo.',
  complementsTitle: 'Materiales y Tutorías Complementarias',
  complementsDescription:
    'Guías de estudio, libros o sesiones de mentoría que el bot ofrecerá para este curso.',
  crossSellingTitle: 'Sugerir guías, libros y mentorías',
  crossSellingDescription:
    'El bot ofrecerá material de estudio o tutorías de apoyo junto al curso o programa.',
}

const SERVICIOS_CONFIG: VerticalCommercialRoleConfig = {
  options: [
    { value: '', label: 'General / Sin rol específico', icon: '📦' },
    { value: 'main', label: 'Servicio Principal', icon: '🛠️' },
    { value: 'accessory', label: 'Insumo / Repuesto Requerido', icon: '🔩' },
    { value: 'service', label: 'Mantenimiento Preventivo', icon: '📋' },
    { value: 'warranty', label: 'Garantía del Trabajo', icon: '🛡️' },
  ],
  helperText:
    'Permite al asistente recomendar insumos requeridos o planes de mantenimiento junto al servicio.',
  complementsTitle: 'Insumos y Mantenimientos Sugeridos',
  complementsDescription:
    'Repuestos necesarios o planes de mantenimiento que el bot sugerirá al cotizar.',
  crossSellingTitle: 'Sugerir insumos y mantenimiento preventivo',
  crossSellingDescription:
    'El bot ofrecerá repuestos requeridos o planes de mantenimiento junto al servicio cotizado.',
}

const GENERIC_CONFIG: VerticalCommercialRoleConfig = {
  options: [
    { value: '', label: 'General / Estándar', icon: '📦' },
    { value: 'main', label: 'Producto Principal', icon: '🏷️' },
    { value: 'accessory', label: 'Accesorio / Complemento', icon: '🔌' },
    { value: 'substitute', label: 'Alternativa / Sustituto', icon: '🔄' },
    { value: 'service', label: 'Servicio / Instalación', icon: '🛠️' },
    { value: 'warranty', label: 'Garantía', icon: '🛡️' },
  ],
  helperText:
    'Permite al asistente recomendar este ítem como producto principal, accesorio o servicio en las sugerencias del bot.',
  complementsTitle: 'Accesorios y Complementos Sugeridos',
  complementsDescription:
    'Productos que el bot sugerirá automáticamente cuando el cliente elija este ítem.',
  crossSellingTitle: 'Sugerir productos complementarios (Cross-selling)',
  crossSellingDescription:
    'El bot sugerirá accesorios o artículos relacionados antes de finalizar el pedido si están configurados.',
}

const VERTICAL_COMMERCIAL_ROLES_MAP: Record<VerticalSlug, VerticalCommercialRoleConfig> = {
  restaurante: RESTAURANTE_CONFIG,
  tienda_repuestos: REPUESTOS_CONFIG,
  bodegon_licoreria: BODEGON_CONFIG,
  ferreteria: FERRETERIA_CONFIG,
  tecnologia: TECNOLOGIA_CONFIG,
  salud: SALUD_CONFIG,
  belleza_estetica: BELLEZA_CONFIG,
  inmobiliaria: INMOBILIARIA_CONFIG,
  turismo_posadas: TURISMO_CONFIG,
  educacion: EDUCACION_CONFIG,
  servicios: SERVICIOS_CONFIG,
  tienda: GENERIC_CONFIG,
  otro: GENERIC_CONFIG,
}

export function getVerticalCommercialRoles(slug?: VerticalSlug | null): VerticalCommercialRoleConfig {
  if (!slug) return GENERIC_CONFIG
  return VERTICAL_COMMERCIAL_ROLES_MAP[slug] || GENERIC_CONFIG
}

export function getVerticalCrossSellingCopy(slug?: VerticalSlug | null): { title: string; description: string } {
  const config = getVerticalCommercialRoles(slug)
  return {
    title: config.crossSellingTitle || GENERIC_CONFIG.crossSellingTitle || 'Sugerir productos complementarios (Cross-selling)',
    description: config.crossSellingDescription || GENERIC_CONFIG.crossSellingDescription || 'El bot sugerirá accesorios o artículos relacionados antes de finalizar el pedido si están configurados.',
  }
}

export function normalizeFrontendCommercialRole(role?: string | null): string {
  if (!role) return ''
  const trimmed = role.trim().toLowerCase()
  if (trimmed === 'core' || trimmed === 'base' || trimmed === 'principal') return 'main'
  return trimmed
}

export function getVerticalRoleLabel(role?: string | null, slug?: VerticalSlug | null): string | null {
  if (!role) return null
  const effectiveRole = normalizeFrontendCommercialRole(role)

  // Check vertical-specific configuration first
  if (slug && VERTICAL_COMMERCIAL_ROLES_MAP[slug]) {
    const verticalMatch = VERTICAL_COMMERCIAL_ROLES_MAP[slug].options.find((opt) => opt.value === effectiveRole)
    if (verticalMatch && verticalMatch.label) {
      return `${verticalMatch.icon} ${verticalMatch.label}`
    }
  }

  // Check generic config
  const genericMatch = GENERIC_CONFIG.options.find((opt) => opt.value === effectiveRole)
  if (genericMatch && genericMatch.label) {
    return `${genericMatch.icon} ${genericMatch.label}`
  }

  // Check restaurant config (as fallback for food terms)
  const restaurantMatch = RESTAURANTE_CONFIG.options.find((opt) => opt.value === role)
  if (restaurantMatch && restaurantMatch.label) {
    return `${restaurantMatch.icon} ${restaurantMatch.label}`
  }

  return role
}
